import React, { useRef, useState } from "react";
import { Info } from "lucide-react";

import { ButtonShadcn as Button } from "@/components/ui/Button";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

/**
 * An "i" icon holding a field's explanatory notes, so long help text stops
 * pushing the form apart.
 *
 * Opens on hover and keyboard focus (the tooltip's own behaviour) and also on
 * click, because a touch screen has no hover and the notes are still something
 * the user needs before acting. A click PINS the notes open and a second click
 * closes them.
 *
 * Hover and pin are two states, not one toggle: a mouse hovers before it
 * clicks, so a single toggle closed the notes on the very click meant to open
 * them. The tooltip also closes itself on a press of its trigger (pointer-down,
 * click, and the content's "pressed outside" — the icon sits outside the
 * content), each of which ran before this toggle and made it read stale state;
 * all three are cancelled for the icon. Any other close the tooltip asks for —
 * pointer leaves, Escape, a tap elsewhere — clears both states.
 *
 * @param {string} label - accessible name of the icon-only trigger.
 * @param {React.ReactNode} children - the notes.
 * @param {"top"|"right"|"bottom"|"left"} [side="top"]
 */
const InfoTip = ({ label, children, side = "top", className }) => {
  const [hovered, setHovered] = useState(false);
  const [pinned, setPinned] = useState(false);
  const triggerRef = useRef(null);

  const handleOpenChange = (next) => {
    setHovered(next);
    if (!next) setPinned(false);
  };

  const togglePinned = () => {
    if (pinned) {
      setPinned(false);
      setHovered(false);
    } else {
      setPinned(true);
    }
  };

  return (
    <TooltipProvider>
      <Tooltip open={pinned || hovered} onOpenChange={handleOpenChange}>
        <TooltipTrigger asChild>
          <Button
            ref={triggerRef}
            variant="ghost"
            size="xs"
            aria-label={label}
            onPointerDown={(e) => e.preventDefault()}
            onClick={(e) => {
              e.preventDefault();
              togglePinned();
            }}
            className={cn("h-5 w-5 shrink-0 rounded-full p-0 text-text-secondary", className)}
          >
            <Info aria-hidden="true" className="h-3.5 w-3.5" />
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side={side}
          sideOffset={4}
          onPointerDownOutside={(e) => {
            if (triggerRef.current?.contains(e.target)) e.preventDefault();
          }}
          className="max-w-xs flex-col items-start gap-2 py-2 leading-snug"
        >
          {children}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default InfoTip;
